import { DateTime } from "luxon";

const displayTimeString = (timestampMilis) => {
  const date = new Date(timestampMilis);
  const hours = `0${date.getUTCHours()}`;
  const minutes = `0${date.getUTCMinutes()}`;
  // Will display time in 01:30 format
  const formattedTime = `${hours.slice(-2)}:${minutes.slice(-2)}`;
  return formattedTime;
};

const displayDateTimeString = (timestampMilis) => {
  const date = new Date(timestampMilis);
  const day = date.getDate();
  const month = date.getMonth() + 1; // Months are 0 indexed
  const hours = `0${date.getUTCHours()}`;
  const minutes = `0${date.getUTCMinutes()}`;
  // Will display time in 01:30 format
  const formattedTime = `${day}/${month} - ${hours.slice(-2)}:${minutes.slice(
    -2
  )}`;
  return formattedTime;
};

const displayPriceText = (
  minPrice,
  maxPrice,
  currency,
  freeText,
  multiplier = 1,
  isIndicativeCalculation = false
) => {
  let priceString = "";
  if (minPrice === maxPrice) {
    priceString = `${currency.name} ${displayPriceBasedOnCurrency(
      minPrice * multiplier,
      currency
    )}`;
  }
  if (minPrice !== maxPrice) {
    const min = minPrice * multiplier;
    const max = maxPrice * multiplier;
    priceString = `${currency.name} ${displayPriceBasedOnCurrency(
      min,
      currency
    )} - ${displayPriceBasedOnCurrency(max, currency)}`;
  }
  if (minPrice === 0 && maxPrice === 0) {
    priceString = freeText;
  }
  return priceString;
};

const displayPricePerPersonText = (
  minPrice,
  maxPrice,
  currency,
  freeText,
  multiplier = 1,
  blindCalculation = false
) => {
  if (minPrice === maxPrice && minPrice !== 0) {
    return `${currency.name} ${displayPriceBasedOnCurrency(
      minPrice,
      currency
    )}`;
  }
  return displayPriceText(minPrice, maxPrice, currency, freeText, 1);
};

const generateCalendarToggleBtnLabel = (
  startDate,
  endDate,
  dayNames,
  monthNames
) => {
  const startDateTime = DateTime.fromMillis(startDate, {
    zone: "Europe/Copenhagen",
  });
  const endDateTime = DateTime.fromMillis(endDate, {
    zone: "Europe/Copenhagen",
  });

  const startDayInWeek =
    startDateTime.weekday === 7 ? 0 : startDateTime.weekday;
  const startDayInMonth = startDateTime.toFormat("dd");
  const endDayInWeek = endDateTime.weekday === 7 ? 0 : endDateTime.weekday;
  const endDayInMonth = endDateTime.toFormat("dd");
  const monthInYear = endDateTime.month;

  const startDayName = dayNames[startDayInWeek]?.substring(0, 3);
  const endDayName = dayNames[endDayInWeek]?.substring(0, 3);

  const label = `${startDayName} ${startDayInMonth} - ${endDayName} ${endDayInMonth} ${
    monthNames[monthInYear - 1]
  }`;

  return label;
};

const displayDateStringDateCardHeader = (dateTimestamp, dayNamesArray = []) => {
  const dayOfWeekDateTime = DateTime.fromMillis(dateTimestamp, {
    zone: "Europe/Copenhagen",
  }).weekday;
  const dayOfWeek = dayOfWeekDateTime === 7 ? 0 : dayOfWeekDateTime;
  // ('0'+currentMinutes).slice(-2) to add zero if number is single digit
  const dayNumber = DateTime.fromMillis(dateTimestamp, {
    zone: "Europe/Copenhagen",
  }).toFormat("dd");
  const finalString = `${dayNumber} ${dayNamesArray[dayOfWeek]?.substring(
    0,
    3
  )}`;
  return finalString;
};

const displayAddonTicketInfoText = (
  addons,
  addonId,
  addonTicket,
  selectedDate,
  texts,
  isActivitiesOnlyFlow = false
) => {
  if (!addonTicket) {
    return "";
  }

  const { qty, entry_no, selectedDate: selectedActivityDate } = addonTicket;

  const selectedAddon = addons.find((addon) => addon.id.toString() === addonId);

  const selectedDay =
    isActivitiesOnlyFlow &&
    selectedAddon?.dates.find(
      (date) =>
        date?.date ===
        (isActivitiesOnlyFlow ? selectedActivityDate : selectedDate)
    )?.formatted_date;

  const selectedTimeslot = selectedAddon?.dates
    .find(
      (date) =>
        date.date ===
        (isActivitiesOnlyFlow ? selectedActivityDate : selectedDate)
    )
    ?.timeslots?.find((timeslot) => timeslot.entry_no === entry_no);

  const time =
    selectedTimeslot && selectedAddon?.auto_select_timeslot === 0
      ? displayTimeString(selectedTimeslot?.timestamp_in_milis)
      : undefined;

  const ticketSingularString = texts?.summary?.ticket || "ticket";
  const ticketPluralString = texts?.summary?.tickets || "tickets";
  const ticketText = qty === 1 ? ticketSingularString : ticketPluralString;

  const timeText = time
    ? `(${[selectedDay, time].filter(Boolean).join(" ")})`
    : "";

  return `${qty} ${ticketText} ${timeText}`;
};

const displayDateInSummary = (selectedDate, dayNames) => {
  if (selectedDate) {
    const date = DateTime.fromMillis(selectedDate, {
      zone: "Europe/Copenhagen",
    });
    const dayOfTheWeek = date.weekday === 7 ? 0 : date.weekday;
    const dayName = dayNames[dayOfTheWeek];
    const formattedDate = DateTime.fromMillis(selectedDate, {
      zone: "Europe/Copenhagen",
    }).toFormat("dd / MM / yyyy");
    return `${dayName} ${formattedDate}`;
  }
};

const displayDateAsIsoDate = (selectedDate) => {
  if (selectedDate) {
    const formattedDate = DateTime.fromMillis(selectedDate, {
      zone: "Europe/Copenhagen",
    }).toFormat("yyyy-MM-dd");
    return formattedDate;
  }
};

const displayTimeRangeFromTimeslots = (timeslots) => {
  if (timeslots.length === 1) {
    const to = timeslots[0]?.start_time?.slice(0, -3) || "00:00";
    const from = timeslots[0]?.end_time?.slice(0, -3) || "00:00";
    return `${to} - ${from}`;
  }

  const timeslotValues = timeslots.map((ts) => ts.timestamp_in_milis);
  const from = Math.min(...timeslotValues);
  const to = Math.max(...timeslotValues);

  const fromFormatted = displayTimeString(from);
  const toFormatted = displayTimeString(to);
  return `${fromFormatted} - ${toFormatted}`;
};

const displayPriceBasedOnCurrency = (price, selectedCurrency) => {
  if (typeof price === "string") {
    const trimmedString = price.replace(/ /g, "");
    if (trimmedString.includes("-")) {
      const prices = trimmedString.split("-");
      const min = prices[0];
      const max = prices[1];
      return `${Math.round(min * selectedCurrency.rate * 100) / 100} - ${
        Math.round(max * selectedCurrency.rate * 100) / 100
      }`;
    }
  }

  return Math.round(price * selectedCurrency.rate * 100) / 100;
};

export const VISUAL_HELPERS = {
  displayAddonTicketInfoText,
  displayDateInSummary,
  displayTimeString,
  displayDateTimeString,
  displayTimeRangeFromTimeslots,
  displayPriceText,
  displayPricePerPersonText,
  displayDateStringDateCardHeader,
  generateCalendarToggleBtnLabel,
  displayDateAsIsoDate,
  displayPriceBasedOnCurrency,
};
